import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getData, postData, putData, deleteData } from '../../../webService/webservice';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import TemplateEditorModal from '../../../components/Campaign/TemplateEditorModal';
import TemplatePreviewModal from '../../../components/Campaign/TemplatePreviewModal';
import './TemplateLibraryPage.css';

const CATEGORIES = [
  'ALL',
  'Promotional',
  'Transactional',
  'Invoice Sharing',
  'Appointment Reminder',
  'Membership Renewal',
  'Loyalty Rewards',
  'Birthday Wishes',
  'Anniversary Wishes',
  'Festival Greetings',
  'Feedback Request',
  'Payment Reminder',
  'Offer & Discount',
  'Custom'
];

const META_CATEGORIES = ['ALL', 'MARKETING', 'UTILITY', 'AUTHENTICATION'];
const META_STATUSES = ['ALL', 'APPROVED', 'PENDING', 'REJECTED', 'PAUSED', 'DISABLED', 'DRAFT'];
const LANGUAGES = [
  { code: 'ALL', label: 'All Languages' },
  { code: 'en_US', label: 'English (US)' },
  { code: 'en_GB', label: 'English (UK)' },
  { code: 'hi', label: 'Hindi (hi)' },
  { code: 'es', label: 'Spanish (es)' },
  { code: 'fr', label: 'French (fr)' },
  { code: 'pt_BR', label: 'Portuguese (pt_BR)' }
];

const TemplateLibraryPage = () => {
  const role = sessionStorage.getItem('role') || localStorage.getItem('role') || '';
  const token = sessionStorage.getItem('Token') || localStorage.getItem('Token') || '';
  const normRole = (role || '').toLowerCase().trim();
  const isSuperAdmin = ['superadmin', 'selfadmin'].includes(normRole);

  const activeHeaderStoreId =
    localStorage.getItem('currentStoreId') ||
    sessionStorage.getItem('currentStoreId') ||
    sessionStorage.getItem('storeId') ||
    localStorage.getItem('storeId') ||
    '';

  const [selectedStoreId, setSelectedStoreId] = useState(activeHeaderStoreId);
  const [stores, setStores] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Filter & Search States
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('ALL');
  const [metaCategory, setMetaCategory] = useState('ALL');
  const channel = 'WhatsApp';
  const [metaStatus, setMetaStatus] = useState('ALL');
  const [language, setLanguage] = useState('ALL');
  const [sortBy, setSortBy] = useState('lastSync');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Modals & Context States
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [activeMenuId, setActiveMenuId] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // Fetch Stores for Super Admin
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isSuperAdmin) {
      const fetchStores = async () => {
        try {
          const res = await getData('stores/stores', { limit: 1000 }, token);
          if (res?.status === 200 || res?.success) {
            const rawStores = res.data?.stores || res.data?.results || (Array.isArray(res.data) ? res.data : []);
            setStores(Array.isArray(rawStores) ? rawStores : []);
          }
        } catch (err) {
          console.error('Error loading stores:', err);
        }
      };
      fetchStores();
    }
  }, [isSuperAdmin, token]);

  // Sync selectedStoreId with header store change
  useEffect(() => {
    const handleStorageChange = () => {
      const currentId = localStorage.getItem('currentStoreId') || sessionStorage.getItem('currentStoreId');
      if (currentId && currentId !== selectedStoreId && !isSuperAdmin) {
        setSelectedStoreId(currentId);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [selectedStoreId, isSuperAdmin]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Fetch Templates API Call
  // ─────────────────────────────────────────────────────────────────────────────
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        category,
        metaCategory,
        channel,
        search,
        metaStatus,
        language,
        page: currentPage,
        limit
      };

      if (selectedStoreId) {
        params.storeId = selectedStoreId;
      }

      const res = await getData('templates', params, token);
      if ((res?.status === 200 || res?.success || res?.data) && res?.data?.templates) {
        setTemplates(res.data.templates);
        setTotalCount(res.data.totalCount || res.data.templates.length);
      } else {
        setTemplates([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error loading templates:', err);
      toast.error('Failed to load template library');
    } finally {
      setLoading(false);
    }
  }, [category, metaCategory, channel, search, metaStatus, language, selectedStoreId, currentPage, limit, token]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Click outside listener for 3-dot dropdown menu
  useEffect(() => {
    const handleOutsideClick = () => setActiveMenuId(null);
    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // Action Handlers
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Sync Templates with Meta Graph API
   */
  const handleSyncMetaTemplates = async () => {
    setSyncing(true);
    toast.info('Fetching Meta WhatsApp Business Templates...', { autoClose: 3000 });
    try {
      const payload = { storeId: selectedStoreId || undefined };
      const res = await postData('templates/sync-meta', payload, token);

      if (res?.status === 200 || res?.success || res?.data) {
        const syncData = res.data || {};
        Swal.fire({
          title: 'Meta Sync Completed!',
          html: `
            <div style="text-align: left; font-size: 14px; line-height: 1.6; color: #334155;">
              <p style="margin-bottom: 8px;">Successfully fetched all message templates from Meta WABA account.</p>
              <div style="background: #f8fafc; padding: 12px; border-radius: 8px; border: 1px solid #e2e8f0; margin-top: 10px;">
                <div>📦 <strong>Total Templates Fetched:</strong> ${syncData.totalFetched || 0}</div>
                <div style="color: #16a34a;">✨ <strong>New Templates Added:</strong> ${syncData.inserted || 0}</div>
                <div style="color: #2563eb;">🔄 <strong>Existing Updated:</strong> ${syncData.updated || 0}</div>
                <div style="font-size: 11.5px; color: #64748b; margin-top: 6px;">Synced at: ${syncData.syncedAt ? new Date(syncData.syncedAt).toLocaleString('en-IN') : 'Just now'}</div>
              </div>
            </div>
          `,
          icon: 'success',
          confirmButtonColor: '#075e54',
          confirmButtonText: 'Great!'
        });
        fetchTemplates();
      } else {
        toast.error(res?.message || 'Meta template synchronization failed');
      }
    } catch (err) {
      console.error('Error syncing Meta templates:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Failed to sync templates with Meta');
    } finally {
      setSyncing(false);
    }
  };

  const handleDuplicate = async (templateId) => {
    try {
      const res = await postData(`templates/${templateId}/duplicate`, {}, token);
      if (res?.status === 200 || res?.success || res?.data) {
        toast.success('Template duplicated successfully');
        fetchTemplates();
      } else {
        toast.error('Failed to duplicate template');
      }
    } catch (err) {
      toast.error('Error duplicating template');
    }
  };

  const handleToggleActive = async (templateId) => {
    try {
      const res = await putData(`templates/${templateId}/toggle-active`, {}, token);
      if (res?.status === 200 || res?.success || res?.data) {
        toast.success(res.message || 'Status updated');
        fetchTemplates();
      }
    } catch (err) {
      toast.error('Error updating status');
    }
  };

  const handleSubmitToMeta = async (templateId, templateName) => {
    try {
      toast.info(`Submitting "${templateName}" to Meta WABA...`);
      const res = await postData(`templates/${templateId}/submit-meta`, {}, token);
      if (res?.status === 200 || res?.success || res?.data) {
        toast.success(res.message || 'Template submitted to Meta successfully!');
        fetchTemplates();
      } else {
        toast.error(res?.message || 'Failed to submit template to Meta');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error submitting template to Meta');
    }
  };

  const handleSyncStatus = async (templateId, templateName) => {
    try {
      toast.info(`Syncing status for "${templateName}" from Meta...`);
      const res = await postData(`templates/${templateId}/sync-status`, {}, token);
      if (res?.status === 200 || res?.success || res?.data) {
        toast.success(res.message || 'Meta status synchronized');
        fetchTemplates();
      } else {
        toast.error(res?.message || 'Failed to sync status');
      }
    } catch (err) {
      toast.error('Error syncing status with Meta');
    }
  };

  const handleDelete = async (templateId, templateName) => {
    const result = await Swal.fire({
      title: 'Deactivate Template?',
      text: `Are you sure you want to deactivate template "${templateName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, Deactivate'
    });

    if (result.isConfirmed) {
      try {
        const res = await deleteData(`templates/${templateId}`, token);
        if (res?.success) {
          toast.success('Template deactivated');
          fetchTemplates();
        } else {
          toast.error(res?.message || 'Failed to deactivate');
        }
      } catch (err) {
        toast.error('Error deleting template');
      }
    }
  };

  // Sort logic for display
  const sortedTemplates = [...templates].sort((a, b) => {
    if (sortBy === 'lastSync') return new Date(b.lastSyncTime || b.updatedAt || 0) - new Date(a.lastSyncTime || a.updatedAt || 0);
    if (sortBy === 'newest') return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    if (sortBy === 'oldest') return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
    if (sortBy === 'name') return (a.templateName || '').localeCompare(b.templateName || '');
    return 0;
  });

  const getStatusBadge = (t) => {
    const mStatus = (t.metaStatus || 'DRAFT').toUpperCase();
    if (mStatus === 'APPROVED') {
      return (
        <span className="badge-chip badge-approved" title="Approved by Meta WABA">
          <Icon icon="mdi:check-circle" /> APPROVED
        </span>
      );
    } else if (mStatus === 'PENDING') {
      return (
        <span className="badge-chip badge-pending" title="Pending Meta Approval">
          <Icon icon="mdi:clock-outline" /> PENDING
        </span>
      );
    } else if (mStatus === 'REJECTED') {
      return (
        <span className="badge-chip badge-rejected" title={t.rejectionReason || 'Rejected by Meta'}>
          <Icon icon="mdi:alert-circle" /> REJECTED
        </span>
      );
    } else if (mStatus === 'PAUSED') {
      return (
        <span className="badge-chip badge-paused" title="Paused by Meta">
          <Icon icon="mdi:pause-circle" /> PAUSED
        </span>
      );
    } else {
      return (
        <span className="badge-chip badge-draft">
          <Icon icon="mdi:file-document-outline" /> {mStatus}
        </span>
      );
    }
  };

  return (
    <div className="product-layout">
      <div className="tmpl-lib-container">
        {/* Breadcrumbs */}
        <div className="tmpl-breadcrumbs">
          <Link to="/dashboard">Home</Link>
          <Icon icon="mdi:chevron-right" />
          <Link to="/campaigns">Campaign</Link>
          <Icon icon="mdi:chevron-right" />
          <span className="crumb-current">Meta WhatsApp Template Library</span>
        </div>

        {/* Header Bar */}
        <div className="tmpl-header-bar">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h1 className="tmpl-header-title">WhatsApp Template Library</h1>
              <span className="meta-connected-badge">
                <Icon icon="mdi:whatsapp" style={{ color: '#25d366', fontSize: '18px' }} /> Meta Graph API Sync
              </span>
            </div>
            <p className="tmpl-header-subtitle">
              Manage and synchronize Meta WhatsApp Message Templates. Automatically sync approved templates from your WhatsApp Business Account (WABA) for direct use in targeted customer campaigns.
            </p>
          </div>

          <div className="tmpl-header-actions">
            {/* Sync Templates Main Button */}
            <button
              className="btn-sync-meta-action"
              onClick={handleSyncMetaTemplates}
              disabled={syncing}
              title="Fetch and sync all WhatsApp templates from Meta WABA Account"
            >
              <Icon icon={syncing ? 'mdi:loading' : 'mdi:sync'} className={syncing ? 'spin' : ''} style={{ fontSize: '20px' }} />
              {syncing ? 'Syncing with Meta...' : 'Sync Templates'}
            </button>

            <button
              className="btn-primary-action"
              onClick={() => {
                setSelectedTemplate(null);
                setIsEditorOpen(true);
              }}
            >
              <Icon icon="mdi:plus-circle" style={{ fontSize: '18px' }} /> Create Template
            </button>
          </div>
        </div>

        {/* Control Filters Bar */}
        <div className="tmpl-controls-bar">
          {/* Search Box */}
          <div className="tmpl-search-box">
            <Icon icon="mdi:magnify" className="search-icon" />
            <input
              type="text"
              className="tmpl-search-input-field"
              placeholder="Search templates by name, text, language, or category..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="tmpl-filters-group">
            {/* Super Admin Store Selector */}
            {isSuperAdmin && stores.length > 0 && (
              <select
                className="tmpl-select-filter store-selector-filter"
                value={selectedStoreId}
                onChange={(e) => setSelectedStoreId(e.target.value)}
                style={{ borderColor: '#075e54', fontWeight: 700, color: '#075e54' }}
              >
                <option value="">🏢 All Stores</option>
                {stores.map((s) => (
                  <option key={s._id} value={s._id}>
                    🏪 {s.storeName}
                  </option>
                ))}
              </select>
            )}

            {/* Meta Category Filter */}
            <select
              className="tmpl-select-filter"
              value={metaCategory}
              onChange={(e) => setMetaCategory(e.target.value)}
            >
              <option value="ALL">All Meta Categories</option>
              {META_CATEGORIES.filter((c) => c !== 'ALL').map((mc) => (
                <option key={mc} value={mc}>
                  Meta: {mc}
                </option>
              ))}
            </select>

            {/* Category Filter */}
            <select className="tmpl-select-filter" value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat === 'ALL' ? 'All Display Categories' : cat}
                </option>
              ))}
            </select>

            {/* Meta Status Filter */}
            <select
              className="tmpl-select-filter"
              value={metaStatus}
              onChange={(e) => setMetaStatus(e.target.value)}
            >
              <option value="ALL">All Meta Statuses</option>
              {META_STATUSES.filter((s) => s !== 'ALL').map((st) => (
                <option key={st} value={st}>
                  Status: {st}
                </option>
              ))}
            </select>


            {/* Sort By */}
            <select className="tmpl-select-filter" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="lastSync">Last Synced / Updated</option>
              <option value="newest">Newest Created</option>
              <option value="oldest">Oldest Created</option>
              <option value="name">Name (A-Z)</option>
            </select>

            {/* View Mode Toggle */}
            <div className="tmpl-view-toggle">
              <button
                className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                title="Grid View"
                onClick={() => setViewMode('grid')}
              >
                <Icon icon="mdi:view-grid-outline" />
              </button>
              <button
                className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                title="List View"
                onClick={() => setViewMode('list')}
              >
                <Icon icon="mdi:view-list-outline" />
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
            <Icon icon="mdi:loading" className="spin" style={{ fontSize: '36px', marginBottom: '12px', color: '#075e54' }} />
            <div style={{ fontWeight: 600, fontSize: '15px' }}>Loading Meta WhatsApp Templates...</div>
          </div>
        ) : sortedTemplates.length === 0 ? (
          /* Empty State */
          <div className="tmpl-empty-state-box">
            <div className="empty-icon-wrapper">
              <Icon icon="mdi:whatsapp" style={{ color: '#25d366' }} />
            </div>
            <h3 className="empty-title">No WhatsApp Templates Found</h3>
            <p className="empty-description">
              No message templates match the current filter or store configuration. Click <strong>"Sync Templates"</strong> to retrieve all existing templates directly from your connected Meta WhatsApp Business Account (WABA ID).
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button className="btn-sync-meta-action" onClick={handleSyncMetaTemplates} disabled={syncing}>
                <Icon icon={syncing ? 'mdi:loading' : 'mdi:sync'} className={syncing ? 'spin' : ''} />
                {syncing ? 'Syncing...' : 'Sync Meta Templates Now'}
              </button>
              <button
                className="btn-primary-action"
                onClick={() => {
                  setSelectedTemplate(null);
                  setIsEditorOpen(true);
                }}
              >
                <Icon icon="mdi:plus-circle" /> Create New Template
              </button>
            </div>
          </div>
        ) : viewMode === 'grid' ? (
          /* Grid View Layout */
          <div className="tmpl-cards-grid">
            {sortedTemplates.map((t) => (
              <div key={t._id} className="tmpl-item-card" style={{ position: 'relative' }}>
                <div>
                  {/* Card Top: Channel + Meta Status Badge */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justify: 'space-between',
                      gap: '8px',
                      marginBottom: '10px'
                    }}
                  >
                    <span className="channel-chip whatsapp">
                      <Icon icon="mdi:whatsapp" /> WhatsApp
                    </span>
                    {getStatusBadge(t)}
                  </div>

                  <div className="tmpl-item-title">{t.templateName}</div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '4px 0 10px 0', flexWrap: 'wrap' }}>
                    <span className="tmpl-item-category">{t.metaCategory || t.category}</span>
                    <span className="lang-tag-chip">{t.language || 'en_US'}</span>
                    {t.qualityRating && t.qualityRating !== 'UNKNOWN' && (
                      <span className="quality-score-tag">{t.qualityRating}</span>
                    )}
                  </div>

                  {/* Body Text Snippet */}
                  <div className="tmpl-item-preview-text">{t.bodyText}</div>

                  {/* Rejection Warning Bar if REJECTED */}
                  {t.metaStatus === 'REJECTED' && (
                    <div className="rejection-alert-strip">
                      <Icon icon="mdi:alert-circle" />
                      <span>{t.rejectionReason || 'Rejected by Meta policy'}</span>
                    </div>
                  )}
                </div>

                <div>
                  <div className="tmpl-card-footer-info">
                    <span style={{ fontSize: '11.5px', color: '#64748b', fontWeight: 600 }}>
                      Synced: {t.lastSyncTime ? new Date(t.lastSyncTime).toLocaleDateString('en-IN') : 'Recent'}
                    </span>

                    <div className="tmpl-card-actions-row">
                      <button
                        className="action-btn-circle"
                        title="Preview WhatsApp Card & Meta Payload"
                        onClick={() => setPreviewTemplate(t)}
                      >
                        <Icon icon="mdi:eye-outline" />
                      </button>
                      <button
                        className="action-btn-circle"
                        title="Edit Template"
                        onClick={() => {
                          setSelectedTemplate(t);
                          setIsEditorOpen(true);
                        }}
                      >
                        <Icon icon="mdi:pencil-outline" />
                      </button>
                      <button
                        className="action-btn-circle"
                        title="More Options"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveMenuId(activeMenuId === t._id ? null : t._id);
                        }}
                      >
                        <Icon icon="mdi:dots-vertical" />
                      </button>
                    </div>
                  </div>

                  {/* 3-Dot Options Dropdown */}
                  {activeMenuId === t._id && (
                    <div
                      className="dropdown-menu-box"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        bottom: '44px',
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                        zIndex: 15,
                        minWidth: '170px',
                        padding: '6px 0'
                      }}
                    >
                      <div
                        className="dropdown-menu-item"
                        style={{ padding: '8px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#0f172a' }}
                        onClick={() => handleSyncStatus(t._id, t.templateName)}
                      >
                        <Icon icon="mdi:refresh" style={{ color: '#16a34a' }} /> Sync Status with Meta
                      </div>
                      <div
                        className="dropdown-menu-item"
                        style={{ padding: '8px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#0f172a' }}
                        onClick={() => handleSubmitToMeta(t._id, t.templateName)}
                      >
                        <Icon icon="mdi:cloud-upload-outline" style={{ color: '#2563eb' }} /> Submit to Meta
                      </div>
                      <div
                        className="dropdown-menu-item"
                        style={{ padding: '8px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#0f172a' }}
                        onClick={() => handleDuplicate(t._id)}
                      >
                        <Icon icon="mdi:content-copy" /> Duplicate
                      </div>
                      <div
                        className="dropdown-menu-item"
                        style={{ padding: '8px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#0f172a' }}
                        onClick={() => handleToggleActive(t._id)}
                      >
                        <Icon icon={t.isActive ? 'mdi:eye-off-outline' : 'mdi:eye-outline'} />
                        {t.isActive ? 'Deactivate' : 'Activate'}
                      </div>
                      <div
                        className="dropdown-menu-item"
                        style={{ padding: '8px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#dc2626' }}
                        onClick={() => handleDelete(t._id, t.templateName)}
                      >
                        <Icon icon="mdi:trash-can-outline" /> Delete Template
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View Layout */
          <div className="tmpl-table-card">
            <table className="tmpl-custom-table">
              <thead>
                <tr>
                  <th>Meta Template Name</th>
                  <th>Meta Category</th>
                  <th>Language</th>
                  <th>Preview Body</th>
                  <th>Meta Status</th>
                  <th>Last Synced</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedTemplates.map((t) => (
                  <tr key={t._id}>
                    <td style={{ fontWeight: 700, color: '#0f172a' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Icon icon="mdi:whatsapp" style={{ color: '#25d366', fontSize: '18px' }} />
                        {t.templateName}
                      </div>
                      {t.metaTemplateId && (
                        <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500 }}>
                          ID: {t.metaTemplateId}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="tmpl-item-category" style={{ margin: 0 }}>
                        {t.metaCategory || t.category}
                      </span>
                    </td>
                    <td>
                      <span className="lang-tag-chip">{t.language || 'en_US'}</span>
                    </td>
                    <td
                      style={{
                        maxWidth: '260px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: '#475569'
                      }}
                    >
                      {t.bodyText}
                    </td>
                    <td>{getStatusBadge(t)}</td>
                    <td style={{ fontSize: '12px', color: '#64748b' }}>
                      {t.lastSyncTime ? new Date(t.lastSyncTime).toLocaleDateString('en-IN') : 'N/A'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="tmpl-card-actions-row" style={{ justifyContent: 'flex-end' }}>
                        <button
                          className="action-btn-circle"
                          title="Preview"
                          onClick={() => setPreviewTemplate(t)}
                        >
                          <Icon icon="mdi:eye-outline" />
                        </button>
                        <button
                          className="action-btn-circle"
                          title="Edit"
                          onClick={() => {
                            setSelectedTemplate(t);
                            setIsEditorOpen(true);
                          }}
                        >
                          <Icon icon="mdi:pencil-outline" />
                        </button>
                        <button
                          className="action-btn-circle"
                          title="Duplicate"
                          onClick={() => handleDuplicate(t._id)}
                        >
                          <Icon icon="mdi:content-copy" />
                        </button>
                        <button
                          className="action-btn-circle"
                          title="Delete"
                          style={{ color: '#dc2626' }}
                          onClick={() => handleDelete(t._id, t.templateName)}
                        >
                          <Icon icon="mdi:trash-can-outline" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Bar */}
        {totalCount > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '24px', padding: '12px 16px', background: '#ffffff', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              Showing {sortedTemplates.length} of <strong>{totalCount}</strong> templates
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <select
                className="tmpl-select-filter"
                style={{ padding: '4px 8px', fontSize: '12px' }}
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>

              <button
                className="btn-secondary-outline"
                style={{ padding: '6px 12px', fontSize: '12px' }}
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              >
                Previous
              </button>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                Page {currentPage} of {Math.ceil(totalCount / limit) || 1}
              </span>
              <button
                className="btn-secondary-outline"
                style={{ padding: '6px 12px', fontSize: '12px' }}
                disabled={currentPage >= Math.ceil(totalCount / limit)}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Modals */}
        <TemplateEditorModal
          isOpen={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
          template={selectedTemplate}
          onSaved={fetchTemplates}
        />

        <TemplatePreviewModal
          isOpen={!!previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          template={previewTemplate}
        />
      </div>
    </div>
  );
};

export default TemplateLibraryPage;